import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import API from "../../lib/api";
import { Shield, Users, Wallet, TrendingUp, Package, ArrowLeft, LayoutDashboard, Image, Megaphone, ShoppingCart, CircleDollarSign, Briefcase, Settings, Code, Eye, AlertTriangle, ChevronDown, ChevronRight, FileText } from "lucide-react";

const ADMIN_GROUPS = [
  { label: "Overview", items: [
    { path: "/admin", label: "Dashboard", icon: LayoutDashboard },
  ]},
  { label: "Users & Security", items: [
    { path: "/admin/users", label: "Users", icon: Users },
    { path: "/admin/anti-abuse", label: "Anti-Abuse", icon: AlertTriangle },
    { path: "/admin/email", label: "Email", icon: Briefcase },
  ]},
  { label: "Game Settings", items: [
    { path: "/admin/economy", label: "Economy", icon: TrendingUp },
    { path: "/admin/game-settings", label: "Game", icon: Settings },
    { path: "/admin/worker-tiers", label: "Worker Tiers", icon: Users },
    { path: "/admin/shop", label: "Shop", icon: ShoppingCart },
    { path: "/admin/tasks", label: "Tasks", icon: Briefcase },
  ]},
  { label: "Payments", items: [
    { path: "/admin/finance", label: "Finance", icon: Wallet },
    { path: "/admin/faucetpay", label: "FaucetPay", icon: CircleDollarSign },
  ]},
  { label: "Content & Ads", items: [
    { path: "/admin/content", label: "Content", icon: Image },
    { path: "/admin/assets", label: "Assets", icon: Megaphone },
    { path: "/admin/ptc", label: "PTC Ads", icon: Eye },
    { path: "/admin/ads", label: "Ad Codes", icon: Code },
    { path: "/admin/offerwalls", label: "Offerwalls", icon: Briefcase },
    { path: "/admin/postback-logs", label: "Postback Logs", icon: FileText },
    { path: "/admin/leaderboard", label: "Leaderboard", icon: Shield },
  ]},
];

function AdminLayout({ children, title }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [openGroups, setOpenGroups] = useState(() => {
    // Auto-open the group that contains the current path
    const open = {};
    ADMIN_GROUPS.forEach(g => {
      if (g.items.some(i => i.path === location.pathname)) open[g.label] = true;
    });
    if (Object.keys(open).length === 0) open["Overview"] = true;
    return open;
  });

  const toggleGroup = (label) => setOpenGroups(prev => ({ ...prev, [label]: !prev[label] }));

  return (
    <div className="min-h-screen bg-[#0B0E14]" data-testid="admin-layout">
      <nav className="glass-panel border-b border-[#2A3241] px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/dashboard" className="flex items-center gap-2 text-[#94A3B8] hover:text-[#F8FAFC] transition-colors" data-testid="admin-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#FFD600]" />
            <span className="text-lg font-bold text-[#F8FAFC]">Admin Panel</span>
          </div>
          <div className="hidden md:flex items-center gap-0.5 ml-6 flex-wrap">
            {ADMIN_GROUPS.map(group => (
              <div key={group.label} className="relative group/nav">
                <button onClick={() => toggleGroup(group.label)}
                  className={`flex items-center gap-1 px-2.5 py-2 text-xs rounded-lg transition-all ${openGroups[group.label] ? "text-[#FFD600]" : "text-[#64748B] hover:text-[#94A3B8]"}`}>
                  {group.label}
                  {openGroups[group.label] ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                </button>
                {openGroups[group.label] && (
                  <div className="flex items-center gap-0.5">
                    {group.items.map(({ path, label, icon: Icon }) => (
                      <Link key={path} to={path} data-testid={`admin-nav-${label.toLowerCase().replace(/\s/g, "-")}`}
                        className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg transition-all ${location.pathname === path ? "bg-[#FFD600]/10 text-[#FFD600]" : "text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[#1C212E]"}`}>
                        <Icon className="w-3.5 h-3.5" /> {label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        <span className="text-sm text-[#64748B]">{user?.email}</span>
      </nav>
      <div className="p-6 max-w-7xl mx-auto animate-fade-in">
        <h1 className="text-2xl font-bold text-[#F8FAFC] mb-6">{title}</h1>
        {children}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await API.get("/admin/dashboard");
        setStats(data);
      } catch {}
    };
    load();
  }, []);

  const cards = stats ? [
    { label: "Total Users", value: stats.total_users, icon: Users, color: "#00F0FF" },
    { label: "Suspicious Users", value: stats.suspicious_users, icon: AlertTriangle, color: "#F59E0B" },
    { label: "Banned Users", value: stats.banned_users, icon: Shield, color: "#EF4444" },
    { label: "Unverified Users", value: stats.unverified_users, icon: Users, color: "#B026FF" },
    { label: "Pending Withdrawals", value: stats.pending_withdrawals, icon: Wallet, color: "#F59E0B" },
    { label: "Pending Deposits", value: stats.pending_deposits, icon: Package, color: "#B026FF" },
    { label: "Total Approved WDs", value: stats.total_approved_withdrawals, icon: TrendingUp, color: "#10B981" },
    { label: "Total WD Amount", value: `$${stats.total_withdrawal_amount?.toFixed(2) || 0}`, icon: Wallet, color: "#FFD600" },
  ] : [];

  return (
    <AdminLayout title="Admin Dashboard">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-dashboard">
        {cards.map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
                <Icon className="w-5 h-5" style={{ color }} />
              </div>
            </div>
            <p className="font-mono-game text-2xl font-bold text-[#F8FAFC]" data-testid={`admin-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>{value}</p>
            <p className="text-xs text-[#64748B] mt-1 tracking-wide uppercase">{label}</p>
          </div>
        ))}
        {!stats && <p className="text-[#94A3B8] col-span-full text-center py-12">Loading stats...</p>}
      </div>
    </AdminLayout>
  );
}

export { AdminLayout };
